import { Component } from '@theme/component';
import { Wishlist } from '@theme/wishlist';

/**
 * A custom element that displays a wishlist icon with a count bubble.
 * Reads the count from window.apa.wishlist.count (set by the wishlist app),
 * falls back to localStorage count for guest users,
 * and listens for the apa:wishlist:count custom event on document.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bubble - The bubble container element.
 * @property {HTMLElement} bubbleCount - The visible count text element.
 * @property {HTMLElement} a11yCount - The screen reader count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['bubble', 'bubbleCount', 'a11yCount'];

  connectedCallback() {
    super.connectedCallback();

    const initialCount = window.apa?.wishlist?.count ?? Wishlist.getCount();
    this.#renderBubble(initialCount);

    document.addEventListener('apa:wishlist:count', this.#onCountUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('apa:wishlist:count', this.#onCountUpdate);
  }

  /**
   * Handles the apa:wishlist:count custom event from the wishlist app.
   * @param {CustomEvent} event
   */
  #onCountUpdate = (event) => {
    const count = event.detail?.count ?? 0;
    this.#renderBubble(count);
  };

  /**
   * Updates the bubble visibility and displayed count.
   * @param {number} count
   */
  #renderBubble(count) {
    const hasItems = count > 0;

    this.refs.bubble.classList.toggle('visually-hidden', !hasItems);
    this.refs.bubbleCount.classList.toggle('hidden', !hasItems);
    this.refs.bubbleCount.textContent = count > 0 && count < 100 ? String(count) : count >= 100 ? '99+' : '';
    this.refs.a11yCount.textContent = String(count);
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
